import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Pencil, Award, IdCard, Download, X, Loader2, MessageCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, StatusBadge, fmtDate, inputCls } from "./AFields";

const CATS = ["", "Participant", "Intern", "Volunteer", "Trainee", "Programme Participant", "Other"];
const STATUSES = ["", "pending", "approved", "rejected", "archived"];
const CERT_TYPES = ["Participation Certificate", "Internship Certificate", "Volunteer Certificate", "Training Certificate", "Appreciation Certificate", "Other"];

export default function Participants() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "participants";
  const [data, setData] = useState({ items: [], total: 0 });
  const [certs, setCerts] = useState([]);
  const [cards, setCards] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null);
  const [certFor, setCertFor] = useState(null);
  const [cardFor, setCardFor] = useState(null);
  const [certForm, setCertForm] = useState({ type: CERT_TYPES[0] });
  const [cardForm, setCardForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/admin/participants/list?q=${encodeURIComponent(q)}&status=${status}&category=${category}&limit=100`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  const loadCerts = () => api.get("/admin/certificates/list").then((r) => setCerts(r.data)).catch(() => {});
  const loadCards = () => api.get("/admin/idcards/list").then((r) => setCards(r.data)).catch(() => {});

  useEffect(() => { load(); loadCerts(); loadCards(); }, [status, category]); // eslint-disable-line

  const saveParticipant = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/participants/${editing.id}`, editing);
      toast.success("Participant updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setPStatus = async (p, st) => {
    try {
      await api.put(`/admin/participants/${p.id}`, { status: st });
      toast.success(`Marked ${st}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const genCert = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/participants/${certFor.id}/certificate`, certForm);
      toast.success(`Certificate ${r.data.cert_no} generated`);
      setCertFor(null);
      loadCerts();
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/certificates/${r.data.id}/pdf`, "_blank");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const genCard = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/participants/${cardFor.id}/idcard`, cardForm);
      toast.success(`ID Card ${r.data.id_no} generated`);
      setCardFor(null);
      loadCards();
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/idcards/${r.data.id}/pdf`, "_blank");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const waLink = (p) => `https://wa.me/${(p.whatsapp || p.mobile || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Namaste ${p.full_name}, greetings from Sahaj Sarathi Foundation regarding your registration ${p.reg_no}.`)}`;

  const TABS = [["participants", "Participants"], ["certificates", "Certificates"], ["idcards", "ID Cards"]];

  return (
    <div data-testid="admin-participants">
      <SEO title="Participants" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Participants, Certificates & ID Cards</h1>
      <div className="mt-5 flex gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setParams({ tab: k })} data-testid={`ptab-${k}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === k ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{l}</button>
        ))}
      </div>

      {tab === "participants" && (
        <>
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-3 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Name / Reg. No / Mobile…" className={`${inputCls} !w-64 !pl-10`} data-testid="participants-search" />
            </div>
            <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid="participants-search-btn">Search</button>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} !w-40`} data-testid="participants-status-filter">
              {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} !w-48`} data-testid="participants-category-filter">
              {CATS.map((c) => <option key={c} value={c}>{c || "All categories"}</option>)}
            </select>
          </div>
          <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Reg. No</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3">Programme</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100 last:border-0 hover:bg-navy-50/30" data-testid={`participant-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{p.reg_no}</td>
                    <td className="px-4 py-3 font-semibold text-navy">{p.full_name}</td>
                    <td className="px-4 py-3">{p.category}</td>
                    <td className="px-4 py-3">{p.mobile || "—"}</td>
                    <td className="max-w-[160px] truncate px-4 py-3">{p.programme || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge value={p.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button title="Edit" onClick={() => setEditing(p)} className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" data-testid={`participant-edit-${p.id}`}><Pencil size={14} /></button>
                        <button title="Generate Certificate" onClick={() => { setCertFor(p); setCertForm({ type: CERT_TYPES[0], programme: p.programme || "", duration: "", issue_date: "" }); }} className="rounded-md border border-gray-200 p-2 text-saffron-600 hover:border-saffron-500" data-testid={`participant-cert-${p.id}`}><Award size={14} /></button>
                        <button title="Generate ID Card" onClick={() => { setCardFor(p); setCardForm({ role: p.category, programme: p.programme || "", valid_from: "", valid_to: "" }); }} className="rounded-md border border-gray-200 p-2 text-brandgreen hover:border-brandgreen" data-testid={`participant-idcard-${p.id}`}><IdCard size={14} /></button>
                        {(p.whatsapp || p.mobile) && <a title="WhatsApp" href={waLink(p)} target="_blank" rel="noreferrer" className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" data-testid={`participant-wa-${p.id}`}><MessageCircle size={14} /></a>}
                      </div>
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No participants found.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "certificates" && (
        <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white" data-testid="certificates-table">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Certificate No</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Programme</th><th className="px-4 py-3">Issue Date</th><th className="px-4 py-3 text-right">PDF</th></tr>
            </thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 last:border-0" data-testid={`cert-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{c.cert_no}</td>
                  <td className="px-4 py-3 font-semibold text-navy">{c.name}</td>
                  <td className="px-4 py-3">{c.type}</td>
                  <td className="px-4 py-3">{c.programme || "—"}</td>
                  <td className="px-4 py-3">{fmtDate(c.issue_date)}</td>
                  <td className="px-4 py-3 text-right">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/certificates/${c.id}/pdf`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy hover:border-saffron-500" data-testid={`cert-pdf-${c.id}`}>
                      <Download size={13} /> PDF
                    </a>
                  </td>
                </tr>
              ))}
              {certs.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No certificates generated yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "idcards" && (
        <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white" data-testid="idcards-table">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">ID No</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Validity</th><th className="px-4 py-3 text-right">PDF</th></tr>
            </thead>
            <tbody>
              {cards.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 last:border-0" data-testid={`idcard-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{c.id_no}</td>
                  <td className="px-4 py-3 font-semibold text-navy">{c.name}</td>
                  <td className="px-4 py-3">{c.role}</td>
                  <td className="px-4 py-3">{fmtDate(c.valid_from)} → {fmtDate(c.valid_to)}</td>
                  <td className="px-4 py-3 text-right">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/idcards/${c.id}/pdf`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy hover:border-saffron-500" data-testid={`idcard-pdf-${c.id}`}>
                      <Download size={13} /> PDF
                    </a>
                  </td>
                </tr>
              ))}
              {cards.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No ID cards generated yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit participant modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Edit — ${editing.full_name}`} testid="participant-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Full Name" value={editing.full_name} onChange={(v) => setEditing({ ...editing, full_name: v })} />
            <SelectF label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={CATS.filter(Boolean)} />
            <TextF label="Mobile" value={editing.mobile} onChange={(v) => setEditing({ ...editing, mobile: v })} />
            <TextF label="WhatsApp" value={editing.whatsapp} onChange={(v) => setEditing({ ...editing, whatsapp: v })} />
            <TextF label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />
            <TextF label="Programme" value={editing.programme} onChange={(v) => setEditing({ ...editing, programme: v })} />
            <TextF label="Completion Date" type="date" value={editing.completion_date} onChange={(v) => setEditing({ ...editing, completion_date: v })} />
            <SelectF label="Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={["pending", "approved", "rejected", "archived"]} />
          </div>
          <div className="mt-5"><AreaF label="Remarks" value={editing.remarks} onChange={(v) => setEditing({ ...editing, remarks: v })} rows={2} /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["approved", "rejected", "archived"].map((st) => (
              <button key={st} onClick={() => { setPStatus(editing, st); setEditing({ ...editing, status: st }); }}
                className="rounded-full border border-gray-200 px-4 py-1.5 text-xs font-bold capitalize text-navy hover:border-saffron-500" data-testid={`participant-${st}`}>{st}</button>
            ))}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={saveParticipant} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="participant-save">{busy && <Loader2 size={15} className="animate-spin" />} Save</button>
          </div>
        </Modal>
      )}

      {/* Certificate modal */}
      {certFor && (
        <Modal onClose={() => setCertFor(null)} title={`Certificate — ${certFor.full_name}`} testid="cert-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <SelectF label="Certificate Type" value={certForm.type} onChange={(v) => setCertForm({ ...certForm, type: v })} options={CERT_TYPES} />
            <TextF label="Programme" value={certForm.programme} onChange={(v) => setCertForm({ ...certForm, programme: v })} />
            <TextF label="Duration" value={certForm.duration} onChange={(v) => setCertForm({ ...certForm, duration: v })} placeholder="e.g. Jan 2026 – Mar 2026" />
            <TextF label="Issue Date" type="date" value={certForm.issue_date} onChange={(v) => setCertForm({ ...certForm, issue_date: v })} />
          </div>
          <p className="mt-4 text-xs text-slate-500">Certificate number and QR verification code are generated automatically. You can edit details before generating.</p>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setCertFor(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={genCert} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="cert-generate">{busy && <Loader2 size={15} className="animate-spin" />} Generate Certificate</button>
          </div>
        </Modal>
      )}

      {/* ID card modal */}
      {cardFor && (
        <Modal onClose={() => setCardFor(null)} title={`ID Card — ${cardFor.full_name}`} testid="idcard-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Role" value={cardForm.role} onChange={(v) => setCardForm({ ...cardForm, role: v })} />
            <TextF label="Programme" value={cardForm.programme} onChange={(v) => setCardForm({ ...cardForm, programme: v })} />
            <TextF label="Valid From" type="date" value={cardForm.valid_from} onChange={(v) => setCardForm({ ...cardForm, valid_from: v })} />
            <TextF label="Valid To" type="date" value={cardForm.valid_to} onChange={(v) => setCardForm({ ...cardForm, valid_to: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setCardFor(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={genCard} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="idcard-generate">{busy && <Loader2 size={15} className="animate-spin" />} Generate ID Card</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-navy/60 p-4 backdrop-blur-sm">
      <div className={`my-8 w-full ${wide ? "max-w-5xl" : "max-w-2xl"} rounded-xl bg-white p-6 shadow-2xl sm:p-8`} data-testid={testid}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-navy">{title}</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-gray-100" aria-label="Close" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}
